import React, { useCallback, useMemo } from "react";
import {
  Grid,
  Button,
  TextField,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import MDBox from "components/MDBox";

const allOption = { id: "all", name: "All", key: "all" };

const STATUS_OPTIONS = [
  { value: "APPROVED",       label: "Approved" },
  { value: "PENDING_REVIEW", label: "Pending Review" },
  { value: "REJECTED",       label: "Rejected" },
  { value: "",               label: "All Statuses" },
];

const MistakesFilters = ({ filters, setFilters, dealers, studios, mistakesList }) => {

  const handleChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, [setFilters]);

  const handleReset = useCallback(() => {
    setFilters({
      startDate: null,
      endDate: null,
      dealer: "all",
      studio: "all",
      mistake: "all",
      status: "APPROVED",
    });
  }, [setFilters]);

  const isFilterActive =
    filters.startDate !== null ||
    filters.endDate !== null ||
    filters.dealer !== "all" ||
    filters.studio !== "all" ||
    filters.mistake !== "all" ||
    filters.status !== "APPROVED";

  const dealerOptions  = useMemo(() => [allOption, ...dealers],     [dealers]);
  const studioOptions  = useMemo(() => [allOption, ...studios],     [studios]);
  const mistakeOptions = useMemo(() => [allOption, ...mistakesList], [mistakesList]);

  const selectedDealer  = useMemo(() => dealerOptions.find(d => d.id  === filters.dealer)  ?? allOption, [dealerOptions,  filters.dealer]);
  const selectedStudio  = useMemo(() => studioOptions.find(s => s.id  === filters.studio)  ?? allOption, [studioOptions,  filters.studio]);
  const selectedMistake = useMemo(() => mistakeOptions.find(m => m.key === filters.mistake) ?? allOption, [mistakeOptions, filters.mistake]);

  return (
    <MDBox p={3} mb={3} mx={2} sx={{ backgroundColor: "#f8f9fa", borderRadius: "12px" }}>
      <Grid container spacing={2} alignItems="center">
        {/* Date Range */}
        <Grid item xs={12} md={4} lg={3}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <MDBox display="flex" gap={1}>
              <DatePicker
                label="From"
                value={filters.startDate}
                onChange={(date) => handleChange("startDate", date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
              <DatePicker
                label="To"
                value={filters.endDate}
                minDate={filters.startDate || undefined}
                onChange={(date) => handleChange("endDate", date)}
                renderInput={(params) => <TextField {...params} size="small" fullWidth />}
              />
            </MDBox>
          </LocalizationProvider>
        </Grid>

        {/* Dealer Filter */}
        <Grid item xs={12} md={4} lg={3}>
            <Autocomplete
                size="small"
                disableClearable
                options={dealerOptions}
                getOptionLabel={(option) => option.name || ""}
                sx={{ width: 200 }}
                value={selectedDealer}
                onChange={(_, newValue) => handleChange("dealer", newValue ? newValue.id : "all")}
                renderInput={(params) => <TextField {...params} label="Dealer" />}
            />
        </Grid>

        {/* Studio Filter */}
        <Grid item xs={12} md={2}>
          <Autocomplete
            disableClearable
            sx={{ width: 150 }}
            size="small"
            options={studioOptions}
            getOptionLabel={(option) => option.name || ""}
            value={selectedStudio}
            onChange={(_, newValue) => handleChange("studio", newValue ? newValue.id : "all")}
            renderInput={(params) => <TextField {...params} label="Studio" />}
          />
        </Grid>

        {/* Mistake Type Filter */}
        <Grid item xs={12} md={2}>
          <Autocomplete
            disableClearable
            sx={{ width: 200 }}
            size="small"
            options={mistakeOptions}
            getOptionLabel={(option) => option.name || ""}
            value={selectedMistake}
            onChange={(_, newValue) => handleChange("mistake", newValue ? newValue.key : "all")}
            renderInput={(params) => <TextField {...params} label="Mistake Type" />}
          />
        </Grid>

        {/* Status Filter */}
        <Grid item xs={12} md={2}>
          <FormControl size="small" sx={{ width: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={filters.status ?? "APPROVED"}
              onChange={(e) => handleChange("status", e.target.value)}
            >
              {STATUS_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Reset Button */}
        <Grid item xs={12} md={1.5}>
            <Button 
                variant="contained"
                disabled={!isFilterActive}
                sx={{ 
                backgroundColor: "#f44336 !important",
                color: "#ffffff !important",
                height: "40px",
                textTransform: "none",
                fontWeight: "bold",
                "&:hover": { backgroundColor: "#d32f2f !important" }
                }}
                fullWidth 
                onClick={handleReset}
            >
                Reset
            </Button>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default MistakesFilters;